'use client';

import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import {cn} from '@/lib/utils';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

/** A blocking confirm portaled to the body — treat it as above this popover. */
function isAlertDialogSurface(target: EventTarget | null): boolean {
  return target instanceof Element
    && Boolean(target.closest('[role="alertdialog"], [data-alert-dialog-overlay]'));
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({
  className,
  align = 'center',
  sideOffset = 4,
  style,
  onInteractOutside,
  onFocusOutside,
  onEscapeKeyDown,
  ...props
}, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'w-72 rounded-lg border border-stone-200 bg-popover p-4 text-popover-foreground shadow-[0_1px_3px_rgba(0,0,0,0.06),0_10px_25px_-6px_rgba(0,0,0,0.14)] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      style={{ zIndex: 9999, ...style }}
      // A confirm opened from inside this popover (Labor → completed-visit
      // balance) portals to the body. Without these, the popover treats that
      // confirm as "outside", dismisses, and unmounts the confirm — or Escape
      // closes Labor first and the user is trapped (RUT-3875).
      onInteractOutside={(event) => {
        if (isAlertDialogSurface(event.target)) event.preventDefault();
        onInteractOutside?.(event);
      }}
      onFocusOutside={(event) => {
        if (isAlertDialogSurface(event.target)) event.preventDefault();
        onFocusOutside?.(event);
      }}
      onEscapeKeyDown={(event) => {
        if (document.querySelector('[role="alertdialog"]')) event.preventDefault();
        onEscapeKeyDown?.(event);
      }}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
